import React, { useContext, useEffect, useRef } from "react";
import { Form, Formik, FormikProps } from "formik";
import * as Yup from "yup";
import Grid from "@material-ui/core/Grid/Grid";
import { AxiosResponse } from "axios";
import CustomButton from "../../../../../components/Button/CustomButton";
import ProductApi from "../../../../../api/services/ProductApi";
import { TesaPrototype } from "../../../../../api/models/product/TesaPrototype";

import PagedResponse from "../../../../../api/models/PagedResponse";
import useEnv from "../../../../../hooks/env";
import PrototypeSampleAddFormType from "../../../../../api/models/product/PrototypeSampleAddFormType";
import ErrorSnackbarContext from "../../../../../contexts/ErrorSnackbarContext";
import { ProductReferenceResponseWithType } from "../../../../../api/models/ProductReferenceResponse";
import useStyles from "./PrototypeSuggestionFormStyle";
import { PrototypeSampleAddFormProps, PrototypeSearchAutocomplete } from "./PrototypeSuggestionForm";
import { getPrototypeProductFullName } from "../../forms/productReferenceHelper";

const createInitialValues = (preset: ProductReferenceResponseWithType | null | undefined) => {
  if (preset) {
    return {
      id: preset?.prototypeProductId,
      name: preset?.prototypeProductName,
      responsibleLaboratory: {
        id: preset?.prototypeProductResponsibleLaboratoryId,
        name: preset?.prototypeProductResponsibleLaboratoryName,
      },
      responsibleDeveloper: null,
      referringTesaProductNart: null,
      description: null,
      creationDate: null,
    } as unknown as TesaPrototype;
  }
  return null;
};

const PrototypeSampleAddFormRequester = (props: PrototypeSampleAddFormProps) => {
  const { halfWidth, handleFormChange, handleSubmit, hideSubmit, preset } = props;
  const { api } = useEnv();
  const productApi = new ProductApi(api);
  const prototypeSearch = useRef<HTMLDivElement & { reset: () => void }>(null);
  const classes = useStyles();
  const { handleHttpError } = useContext(ErrorSnackbarContext);

  const yupValidationSchema = Yup.object().shape({
    prototype: Yup.mixed().required("Please select"),
  });

  return (
    <Formik<PrototypeSampleAddFormType>
      initialValues={{
        prototype: createInitialValues(preset),
      }}
      onSubmit={(values) => {
        if (handleSubmit) {
          handleSubmit(values as PrototypeSampleAddFormType);
        }
      }}
      validationSchema={yupValidationSchema}
    >
      {(formikProps: FormikProps<PrototypeSampleAddFormType>): JSX.Element => {
        const { errors, isSubmitting, touched, values, setFieldValue } = formikProps;

        useEffect(() => {
          if (handleFormChange) {
            handleFormChange(values);
          }
        }, [values]);

        return (
          <Form>
            <Grid container justify="flex-start" direction="row" spacing={4}>
              <Grid item xs={halfWidth ? 6 : 12}>
                <PrototypeSearchAutocomplete
                  autoFocus
                  ref={prototypeSearch}
                  inputLabel="Prototype Name"
                  optionFetch={(query: string): Promise<PagedResponse<TesaPrototype>> => {
                    if (!query) {
                      return Promise.resolve({
                        data: [],
                        totalPages: 1,
                        totalItems: 0,
                      } as unknown as PagedResponse<TesaPrototype>);
                    }
                    return productApi
                      .getPrototypes(query)
                      .then((res: AxiosResponse) => {
                        return res?.data;
                      })
                      .catch(handleHttpError);
                  }}
                  onChange={(e, value) => {
                    if (value === null) {
                      const name = (e.target as HTMLInputElement).value || null;
                      setFieldValue("prototype", name && { name });
                    } else {
                      setFieldValue("prototype", value);
                    }
                  }}
                  mandatory
                  map={(value): { key: string; label: string } => ({
                    key: `${value.id}`,
                    label: getPrototypeProductFullName(value.name, value.responsibleLaboratory?.name),
                  })}
                  value={values.prototype}
                  error={!!(errors.prototype && touched.prototype)}
                />
              </Grid>
            </Grid>
            {!hideSubmit && (
              <Grid container item direction="row" justify="flex-end">
                <CustomButton
                  className={classes.marginTop}
                  disabled={isSubmitting}
                  primary
                  text="Add Prototype"
                  type="submit"
                />
              </Grid>
            )}
          </Form>
        );
      }}
    </Formik>
  );
};

export default PrototypeSampleAddFormRequester;
